import { ImageListItemStatus } from "./image-list-item-hooks";
import toast from "@/lib/components/Toast";
import {
  IconChevronLeft,
  IconChevronRight,
  IconTrash,
} from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import GradientBorder from "@/lib/components/GradientBorder";
import { Checkbox } from "@/lib/components/Checkbox";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";

type Props = {
  status: ImageListItemStatus;
  selectTimelineItemForAnimation: (
    externalId: string,
    selected: boolean,
  ) => void;
  removeCompositionImage: (externalId: string) => void;
  timelineItem: CreativeProjectTimelineItem;
  setAnimatedImage: (
    timelineItemExternalId: string,
    animatedImageExternalId: string,
  ) => void;
};

export default function ImageOverlayControls({
  status,
  selectTimelineItemForAnimation,
  removeCompositionImage,
  timelineItem,
  setAnimatedImage,
}: Props) {
  const isSelected = status === "SELECTED";

  const handleSelectImageForAnimation = useCallback(() => {
    if (isSelected && timelineItem.animatedImageExternalIds.length === 0) {
      toast.info("Image has to be animated at least once");
      return;
    }

    selectTimelineItemForAnimation(timelineItem.externalId, !isSelected);
  }, [
    isSelected,
    timelineItem.animatedImageExternalIds.length,
    timelineItem.externalId,
    selectTimelineItemForAnimation,
  ]);

  return (
    <>
      <GradientBorder
        isActive={isSelected}
        className="-top-0.5 -right-0.5 -bottom-0.5 -left-0.5 rounded-2xl"
      />
      <div className="absolute top-0 right-0 left-0 z-20 flex h-14 rounded-t-xl bg-linear-to-b from-black/30 via-black/25 to-black/0 px-4 pt-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={handleSelectImageForAnimation}
        />

        <p
          className={`ml-2 cursor-pointer self-start text-sm font-medium text-white select-none ${isSelected ? "opacity-100" : "opacity-90"}`}
          onClick={handleSelectImageForAnimation}
        >
          {isSelected ? "Selected for animation" : "Re-animate"}
        </p>
      </div>

      <div className="absolute right-0 bottom-0 left-0 z-20 flex h-14 items-end justify-between rounded-b-xl bg-linear-to-t from-black/30 via-black/25 to-black/0 pr-2.5 pb-3 pl-4">
        <button
          className="-ml-1 flex h-6 w-6 cursor-pointer items-center justify-center text-white"
          onClick={(e) => {
            e.stopPropagation();
            removeCompositionImage(timelineItem.externalId);
          }}
        >
          <IconTrash size={20} strokeWidth={1.5} />
        </button>
        <AnimatedImageSwitcher
          timelineItem={timelineItem}
          setAnimatedImage={setAnimatedImage}
        />
      </div>
    </>
  );
}

type AnimatedImageSwitcherProps = {
  timelineItem: CreativeProjectTimelineItem;
  setAnimatedImage: (
    timelineItemExternalId: string,
    animatedImageExternalId: string,
  ) => void;
};

function AnimatedImageSwitcher({
  timelineItem,
  setAnimatedImage,
}: AnimatedImageSwitcherProps) {
  const currentIndex = useMemo(() => {
    return timelineItem.animatedImageExternalIds.findIndex(
      (externalId) => externalId === timelineItem.animatedImageExternalId,
    );
  }, [timelineItem]);

  const handleClick = useCallback(
    (index: number) => {
      const animatedImageExternalId =
        timelineItem.animatedImageExternalIds[index];

      if (!animatedImageExternalId) {
        return;
      }

      setAnimatedImage(timelineItem.externalId, animatedImageExternalId);
    },
    [setAnimatedImage, timelineItem],
  );

  if (
    timelineItem.animatedImageExternalIds.length < 2 ||
    !timelineItem.animatedImageExternalId ||
    currentIndex === -1
  ) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 text-white">
      <button
        className="flex h-6 w-6 cursor-pointer items-center justify-center outline-none disabled:cursor-default disabled:opacity-50"
        disabled={currentIndex === 0}
        onClick={() => handleClick(currentIndex - 1)}
      >
        <IconChevronLeft size={18} strokeWidth={1.5} />
      </button>

      <p className="min-w-2 text-sm font-medium">{currentIndex + 1}</p>
      <button
        className="flex h-6 w-6 cursor-pointer items-center justify-center outline-none disabled:cursor-default disabled:opacity-50"
        disabled={
          currentIndex === timelineItem.animatedImageExternalIds.length - 1
        }
        onClick={() => handleClick(currentIndex + 1)}
      >
        <IconChevronRight size={18} strokeWidth={1.5} />
      </button>
    </div>
  );
}
